import { MaterialService } from './material.service';
import { Injectable } from '@angular/core';
import { HttpClient, HttpParams } from '@angular/common/http';
import { environment } from '../../../../environments/environment';
import { KiwixiService } from '../kiwixi/kiwixi.service';
import { KiwixiGlobals } from '../kiwixi/kiwixi.globals';
import { Router } from '@angular/router';
import { Observable } from 'rxjs';
import { catchError, map } from 'rxjs/operators';
import { Examination, ExaminationList } from '../model/examination.model';
import { Student } from '../model/student.model';
import { ExaminationVariants, ExaminationVariantsList } from '../model/examinationVariants.model';
import { ExaminationSlide, ExaminationSlideList, ExaminationSlideText, ExaminationSlideTextList } from '../model/examinationSlides.model';
import { AuthenticationService } from './authentification.service';
import { ExaminationSession } from '../model/examination-session.model';

@Injectable()
export class ExaminationService extends KiwixiService {

    apiExamination = environment.server + 'examinations/';


    getExaminationStatus() {
        return [
            {
                label: 'Brouillon',
                code: 'draft',
                valid: true
            },
            {
                label: 'A valider',
                code: 'moderate',
                valid: true
            },
            {
                label: 'Publiée',
                code: 'published',
                valid: true
            },
            {
                label: 'Désactivée',
                code: 'disabled',
                valid: true
            }
        ];
    }

    extractIdFromUrl(url) {
        if (url) {
            return parseInt(url.split('/')[url.split('/').length - 2], 0);
        }
        return null;
    }

    constructor(
        private _http: HttpClient,
        private _router: Router,
        kiwixiGlobals: KiwixiGlobals,
        private materialService: MaterialService,
        private authentificationService: AuthenticationService
    ) {
        super(_http, _router, kiwixiGlobals, materialService);
    }

    getExaminationById(_id): Observable<Examination> {
        const url = this.apiExamination + _id + '/' + '?salt=' + Math.random();
        return this.getExaminationByUrl(url);
    }

    getExaminationByUrl(_url): Observable<Examination> {
        const header = this.createHttpClientAuthorizationHeader();
        return this._http.get<Examination>(_url + '?salt=' + Math.random(), {
            headers: header
        })
            .pipe(
                catchError(this.handleErrorObservable<any>(`getExamination`))
            );
    }

    getExaminations(params: HttpParams): Observable<ExaminationList> {
        const header = this.createHttpClientAuthorizationHeader();
        return this._http.get<ExaminationList>(this.apiExamination, {
            headers: header,
            params: params
        })
            .pipe(
                map(_Examinations => _Examinations),
                catchError(this.handleErrorObservable<ExaminationList>(`getObservableExaminations`))
            );
    }

    getExaminationsByUrl(_url): Observable<ExaminationList> {
        const header = this.createHttpClientAuthorizationHeader();
        return this._http.get<ExaminationList>(_url + '?salt=' + Math.random(), {
            headers: header
        })
            .pipe(
                catchError(this.handleErrorObservable<ExaminationList>(`getExamination`))
            );
    }

    postExamination(params) {
        const headers = this.createHttpClientAuthorizationHeader();
        return this._http.post<Examination>(this.apiExamination, params, { headers: headers })
            .pipe(
                catchError(this.handleErrorObservable<Examination>(`Création d'une fiche`)),
            );
    }

    patchExamination(_id, params) {
        const url = this.apiExamination + _id + '/';
        const headers = this.createHttpClientAuthorizationHeader();
        return this._http.patch<Examination>(url, params, { headers: headers })
            .pipe(
                catchError(this.handleErrorObservable<Examination>(`Création d'une fiche`)),
            );
    }

    deleteExamination(id): Observable<Examination> {
        const header = this.createHttpClientAuthorizationHeader();
        return this._http.delete<Examination>(this.apiExamination + id + '/', {
            headers: header,
        })
            .pipe(
                catchError(this.handleErrorObservable<Examination>(`delete ExaminationSlide`))
            );
    }

    /**
     * Ouvre une session d'examen (classique ou IA)
     * @param email - Email du candidat
     * @param lilateId - ID du LILATE
     * @param access_token - Token d'accès
     * @param target - Target de la fenêtre (_self, _blank)
     * @param isAi - (Optionnel) Si true, c'est un LILATE IA
     * @param status - (Optionnel) Statut du LILATE (pending, ongoing, done)
     */
    openSession(email: string, lilateId: number, access_token: string, target: string, isAi?: boolean, status?: string) {
        // Code à usage unique (API-R1) : l'application d'examen l'échange contre une
        // session sur la persona `email`, avec le site et le rôle de l'application
        // d'origine. Aucun jeton dans l'URL (`access_token` n'est plus transmis).
        this.examinationHandoff(email).subscribe(res => {
            let link = environment.urls.exam
                + '/?code=' + encodeURIComponent(res.code)
                + '&lilate_id=' + lilateId
                + '&impersonate=true';

            // Ajouter params IA si applicable
            if (isAi) {
                link += '&ia=true';
            }
            if (status) {
                link += '&status=' + status;
            }

            window.open(link, target);
        });
    }

    /**
    * Methode pour ouvrir la session.
    */
    openExamPreview(email, variantId, access_token) {
        this.examinationHandoff(email).subscribe(res => {
            const link = environment.urls.exam
                + '/?code=' + encodeURIComponent(res.code)
                + '&variant_id=' + variantId
                + '&impersonate=true';
            window.open(link, '_blank');
        });
    }

    /** Code d'ouverture de l'application d'examen, sur le site et le rôle de l'application courante. */
    private examinationHandoff(email: string) {
        return this.authentificationService.handoff({
            persona: email,
            site: localStorage.getItem('site'),
            role: localStorage.getItem('default_role'),
        });
    }

    postExaminationSlideResult(params): Observable<any> {
        const headers = this.createHttpClientAuthorizationHeader();
        return this._http.post<any>(this.apiExamination + 'save_slide_result/', params, { headers: headers })
            .pipe(
                catchError(this.handleErrorObservable<any>(`Création d'une réponse a une slide`)),
            );
    }
}
